import { useMutation, useQuery } from "@apollo/client";
import { Editor } from "@tiptap/react";
import copy from "copy-to-clipboard";
import { compact } from "lodash";
import { useContext, useEffect, useState } from "react";
import { matchPath, useLocation } from "react-router-dom";
import {
  ChatbotRoleEnum,
  EditSuggestedAssessmentResponseMutationMutation,
  EditSuggestedAssessmentResponseMutationMutationVariables,
  GetAskAiChatSessionQueryQuery,
  GetAskAiChatSessionQueryQueryVariables,
} from "types/graphql-schema";
import { TFLocationState } from "types/topicflow";

import { assessmentContextSidebarVar } from "@apps/assessments/components/context-sidebar/context-sidebar-context";
import useLabel from "@apps/use-label/use-label";
import cache, {
  errorNotificationVar,
  successNotificationVar,
} from "@cache/cache";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import InfoTooltip from "@components/tooltip/info-tooltip";
import { getTopicflowIcon } from "@components/topicflow-icon/topicflow-icon-helpers";
import { pluralize, sanitizeMarkdown } from "@helpers/string";

import { ChatSessionContext } from "../ask-ai-context";
import editSuggestedAssessmentResponseMutation from "../graphql/edit-suggested-assessment-response-mutation";
import getAskAiSessionQuery from "../graphql/get-ask-ai-session-query";
import {
  createChatMessageNode,
  createSuggestedAssessmentResponseTag,
} from "../helpers";
import AskAIMessageMarkdown from "./ask-ai-message-markdown";
import AskAICitationModal from "./citations-modal";

/** Minimal citation shape accepted from parsed content; the citation modal hydrates full data via GQL. */
type MinimalCitation = {
  title: string;
  url: string;
  sourceId: string;
  sourceProvider?: string | null;
  sourceType?: string | null;
};

export const AskAISuggestedAssessmentResponseStreaming = ({
  suggestedResponse,
}: {
  suggestedResponse?: string;
}) => {
  const label = useLabel();

  return (
    <div className="w-full flex flex-col gap-2">
      <div className="font-medium text-sm text-gray-700 flex items-center gap-1.5">
        Suggested Responses
        <InfoTooltip
          text={`Suggested responses are generated using data from Topicflow and external integrations. These sources are visible to the reviewer during ${label("review", { pluralize: true })}, and to management when the ${label("review")} is delivered.`}
        />
      </div>
      <div className="border rounded-lg py-4 px-4 flex flex-col gap-4">
        <div className="flex items-center justify-between gap-2">
          <div className="text-gray-500">Preparing sources...</div>
          <div className="bg-gray-100 animate-pulse rounded-md h-6 w-32" />
        </div>
        {suggestedResponse ? (
          <div className="prose prose-sm max-w-none">
            <AskAIMessageMarkdown>
              {sanitizeMarkdown(suggestedResponse)}
            </AskAIMessageMarkdown>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <div className="bg-gray-100 animate-pulse rounded-md h-4 w-full" />
            <div className="bg-gray-100 animate-pulse rounded-md h-4 w-11/12" />
            <div className="bg-gray-100 animate-pulse rounded-md h-4 w-2/3" />
          </div>
        )}
      </div>
    </div>
  );
};

export type SuggestedAssessmentResponsesPlaceholderType = {
  responses: {
    suggestedResponse: string;
    citations: MinimalCitation[];
    suggestionId: string | null;
  }[];
};

const AskAISuggestedAssessmentResponses = ({
  suggestedAssessmentResponses,
  questionId,
  canBeEdited = false,
  chatSessionId,
  messageId,
}: {
  suggestedAssessmentResponses: SuggestedAssessmentResponsesPlaceholderType["responses"];
  questionId?: number | null;
  canBeEdited?: boolean;
  chatSessionId?: number;
  messageId: string;
}) => {
  const [showCitationsModal, setShowCitationsModal] = useState(false);
  const responses = compact(suggestedAssessmentResponses);
  const [index, setIndex] = useState(0);
  const displayedResponse = responses[index];
  const citations = compact(displayedResponse?.citations || []);
  const {
    assessmentId,
    assessmentQuestionId,
    setCurrentSuggestedAssessmentResponseId,
  } = useContext(ChatSessionContext);
  const label = useLabel();
  const location = useLocation<TFLocationState>();

  const { data: cachedChatSession } = useQuery<
    GetAskAiChatSessionQueryQuery,
    GetAskAiChatSessionQueryQueryVariables
  >(getAskAiSessionQuery, {
    variables: { chatSessionId: chatSessionId || 0 },
    fetchPolicy: "cache-only",
    skip: !chatSessionId,
  });
  const originQuestionId =
    questionId || cachedChatSession?.chatSession?.assessmentQuestion?.id;
  useEffect(() => {
    if (
      canBeEdited ||
      !originQuestionId ||
      originQuestionId !== assessmentQuestionId
    )
      return;
    setCurrentSuggestedAssessmentResponseId?.(
      displayedResponse?.suggestionId || null,
    );
    return () => {
      setCurrentSuggestedAssessmentResponseId?.(null);
    };
  }, [
    canBeEdited,
    originQuestionId,
    assessmentQuestionId,
    displayedResponse?.suggestionId,
    setCurrentSuggestedAssessmentResponseId,
  ]);

  const isOnAssessmentPage = matchPath(
    location.pathname,
    "/assessments/assessment/:assessmentId",
  );

  const [editSuggestedAssessmentResponse, { loading: loadingEditResponse }] =
    useMutation<
      EditSuggestedAssessmentResponseMutationMutation,
      EditSuggestedAssessmentResponseMutationMutationVariables
    >(editSuggestedAssessmentResponseMutation);

  const scrollThreadToBottom = () => {
    requestAnimationFrame(() => {
      const scrollContainer = document.getElementById(
        "ask-ai-scroll-container",
      );
      scrollContainer?.scrollTo({
        top: scrollContainer.scrollHeight,
        behavior: "smooth",
      });
    });
  };

  const handleClickEdit = () => {
    if (
      loadingEditResponse ||
      !chatSessionId ||
      !displayedResponse?.suggestionId
    )
      return;
    const cachedChatSession = cache.readQuery<
      GetAskAiChatSessionQueryQuery,
      GetAskAiChatSessionQueryQueryVariables
    >({
      query: getAskAiSessionQuery,
      variables: { chatSessionId },
    });
    const content = createSuggestedAssessmentResponseTag({
      suggestedResponse: displayedResponse?.suggestedResponse || "",
      suggestionId: displayedResponse?.suggestionId || "",
      citations: displayedResponse?.citations || [],
    });
    const optimisticMessage = createChatMessageNode({
      content,
      assessmentQuestionId: originQuestionId,
      role: ChatbotRoleEnum.Assistant,
    });

    const appendMessageToCachedSession = (
      message: ReturnType<typeof createChatMessageNode>,
    ) => {
      const currentChatSession = cache.readQuery<
        GetAskAiChatSessionQueryQuery,
        GetAskAiChatSessionQueryQueryVariables
      >({
        query: getAskAiSessionQuery,
        variables: { chatSessionId },
      });
      const chatSession = currentChatSession?.chatSession;
      if (!chatSession?.messages) return;
      const existingEdges = chatSession.messages.edges || [];
      const alreadyExists = existingEdges.some(
        (edge) => edge?.node?.id === message.id,
      );
      if (alreadyExists) return;

      cache.writeQuery<
        GetAskAiChatSessionQueryQuery,
        GetAskAiChatSessionQueryQueryVariables
      >({
        query: getAskAiSessionQuery,
        variables: { chatSessionId },
        data: {
          __typename: "Query",
          chatSession: {
            ...chatSession,
            messages: {
              ...chatSession.messages,
              edges: [
                ...existingEdges,
                {
                  __typename: "ChatMessageEdge",
                  node: message,
                },
              ],
            },
          },
        },
      });
    };

    if (assessmentId) {
      assessmentContextSidebarVar(
        originQuestionId
          ? { assessmentId, questionId: originQuestionId }
          : null,
      );
    }
    editSuggestedAssessmentResponse({
      variables: {
        chatSessionId,
        messageId,
        suggestionId: displayedResponse?.suggestionId,
      },
      update: (_, result) => {
        const edges =
          result.data?.editSuggestedAssessmentResponse?.chatSession?.messages
            ?.edges || [];
        const lastEdge = edges[edges.length - 1];
        appendMessageToCachedSession(lastEdge?.node || optimisticMessage);
      },
      optimisticResponse: {
        __typename: "Mutation",
        editSuggestedAssessmentResponse: {
          __typename: "EditSuggestedAssessmentResponseMutation",
          chatSession: cachedChatSession?.chatSession
            ? {
                ...(cachedChatSession?.chatSession || {}),
                messages: {
                  __typename: "ChatMessageConnection",
                  edges: [
                    ...(cachedChatSession?.chatSession?.messages?.edges || []),
                    {
                      __typename: "ChatMessageEdge",
                      node: optimisticMessage,
                    },
                  ],
                },
              }
            : undefined,
        },
      },
      onCompleted: scrollThreadToBottom,
    });
    scrollThreadToBottom();
  };

  const handleClickInsertResponse = () => {
    const suggestedResponse = displayedResponse?.suggestedResponse || "";
    if (!suggestedResponse) return;
    const elSelector = `#assessment-question-${originQuestionId} .ProseMirror[contenteditable="true"]`;
    const element = originQuestionId
      ? document.querySelector<HTMLDivElement & { editor: Editor }>(elSelector)
      : null;
    if (element && element.editor) {
      // insert content at the end of the editor and focus on it
      const contentToInsert = `${
        element.editor.isEmpty ? "" : "\n\n"
      }${suggestedResponse}`;
      element.editor
        .chain()
        .focus("end")
        .insertContent(contentToInsert)
        .focus()
        .run();
    } else if (copy(suggestedResponse)) {
      successNotificationVar({
        title: "Response Copied to Clipboard",
        description: "Paste it into the question you want to answer.",
        timeout: 10000,
      });
    } else {
      errorNotificationVar({
        title:
          "Couldn’t copy the response. Select the text and copy it manually.",
      });
    }
  };

  return (
    <div className="w-full flex flex-col gap-2">
      <div className="flex items-center justify-between gap-4">
        <div className="font-medium text-sm text-gray-700 flex items-center gap-1.5">
          Suggested Responses
          <InfoTooltip
            text={`Suggested responses are generated using data from Topicflow and external integrations. These sources are visible to the reviewer during ${label("review", { pluralize: true })}, and to management when the ${label("review")} is delivered.`}
          />
        </div>
        {responses.length > 1 && (
          <div className="flex items-center gap-2">
            <Button
              icon={getTopicflowIcon("arrowLeft")}
              theme={buttonTheme.iconGray}
              size={ButtonSize.small}
              onClick={() => setIndex(index - 1)}
              disabled={index === 0}
            />
            <span className="text-gray-500">
              {index + 1}/{responses.length}
            </span>
            <Button
              icon={getTopicflowIcon("arrowRight")}
              theme={buttonTheme.iconGray}
              size={ButtonSize.small}
              onClick={() => setIndex(index + 1)}
              disabled={index === responses.length - 1}
            />
          </div>
        )}
      </div>
      <div className="border rounded-lg py-4 px-4 flex flex-col gap-4">
        <div className="flex items-center justify-between gap-2">
          <div>
            <button
              className="text-gray-500"
              onClick={() => setShowCitationsModal(true)}
              disabled={citations.length === 0}
            >
              {`${citations.length} ${pluralize(
                "source",
                citations.length,
              )} used`}
            </button>
          </div>
          <div className="flex items-center gap-2">
            {canBeEdited && chatSessionId && (
              <Button
                text="Edit"
                size={ButtonSize.medium}
                onClick={handleClickEdit}
              />
            )}
            <Button
              text={originQuestionId ? "Insert Response" : "Copy Response"}
              theme={buttonTheme.lightBlue}
              size={ButtonSize.medium}
              onClick={handleClickInsertResponse}
              disabled={!!originQuestionId && !isOnAssessmentPage}
              tooltip={
                originQuestionId && !isOnAssessmentPage
                  ? `You can only perform "Insert Response" on the ${label("review")} submission page`
                  : undefined
              }
            />
          </div>
        </div>
        <div className="prose prose-sm max-w-none">
          <AskAIMessageMarkdown>
            {sanitizeMarkdown(displayedResponse?.suggestedResponse || "")}
          </AskAIMessageMarkdown>
        </div>
      </div>
      {showCitationsModal && (
        <AskAICitationModal
          citations={citations}
          chatSessionId={chatSessionId}
          onClose={() => setShowCitationsModal(false)}
        />
      )}
      <div className="mt-2 prose prose-sm max-w-none">
        Let me know if you want me to tweak the wording or if you have other
        sources you'd like to use.
      </div>
    </div>
  );
};

export default AskAISuggestedAssessmentResponses;
